import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	EmptyStateComponent,
	IMapMarker,
	InputSearchComponent,
	ISelect,
	MapComponent,
	SelectComponent,
} from '@tecalisdev/infinity';
import { formatDeviceCreatedAtDisplay } from '../../../core/utils/utils';
import {
	deviceBrowserSummary,
	deviceDisplayName,
	devicesIpBlockUnlockMode,
	IProcessDeviceView,
	IProcessLocationView,
	ProcessDeviceBlockUnlockEvent,
	resolveDeviceIndexForLocation,
} from '../../../processes/utils/process-devices.util';
import { MapDeviceInfoComponent } from './map-device-info.component';

@Component({
	selector: 'app-maps-localization',
	imports: [
		GoogleMapsModule,
		TranslatePipe,
		MapComponent,
		EmptyStateComponent,
		InputSearchComponent,
		SelectComponent,
		ReactiveFormsModule,
	],
	templateUrl: './maps-localization.component.html',
	styleUrl: './maps-localization.component.scss',
})
export class MapsLocalizationComponent implements OnChanges {
	@Input() locations: IProcessLocationView[] = [];
	/** Dispositivos del proceso; correlacionan el nº del marcador con la lista. */
	@Input() devices: IProcessDeviceView[] = [];
	/** IPs bloqueadas del proceso (`detail.locks.blocked_ips`). */
	@Input() blockedIps: { ip?: string }[] = [];
	@Input() canBlockIp = false;
	@Input() canUnblockIp = false;
	/**
	 * Buscador por nombre de dispositivo.
	 * Se pinta fuera de `tec-map` porque el DS solo muestra search si `markers.length > 1`.
	 */
	@Input() enableSearch = true;

	@Output() viewDeviceDetails = new EventEmitter<IProcessDeviceView>();
	@Output() blockUnlockClick = new EventEmitter<ProcessDeviceBlockUnlockEvent>();

	trans = inject(TranslateService);

	markerPositions: IMapMarker[] = [];
	filteredMarkers: IMapMarker[] = [];
	searchText = '';
	showSearchMenu = false;
	selectedOption = new FormControl<ISelect | null>(null);

	get showSearch(): boolean {
		return this.enableSearch && this.markerPositions.length > 0;
	}

	get searchOptions(): ISelect[] {
		const filter = this.searchText.toLowerCase();
		return this.markerPositions
			.filter((marker) => !filter || marker.title?.toLowerCase().includes(filter))
			.map((marker) => ({ id: marker.id, label: marker.title }));
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (
			changes['locations'] ||
			changes['devices'] ||
			changes['blockedIps'] ||
			changes['canBlockIp'] ||
			changes['canUnblockIp']
		) {
			this.addMarkers();
		}
	}

	addMarkers(): void {
		const locations = this.locations ?? [];
		const devices = this.devices ?? [];

		this.markerPositions = locations
			.map((loc, locationIndex) => {
				const lat = Number(loc.lat);
				const lng = Number(loc.lon);
				if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
					return null;
				}

				const deviceIndex = resolveDeviceIndexForLocation(locationIndex, devices.length);
				const device = deviceIndex !== null ? devices[deviceIndex] : undefined;
				const ordinal = (deviceIndex ?? locationIndex) + 1;
				const title =
					deviceIndex !== null
						? deviceDisplayName((key) => this.trans.instant(key), deviceIndex)
						: `${this.trans.instant('PROCESSES.DETAIL.LOCATION')} ${ordinal}`;

				const lockMode = device
					? devicesIpBlockUnlockMode(device.ip, this.blockedIps, this.canBlockIp, this.canUnblockIp)
					: null;

				const rows = [];
				if (device) {
					rows.push({
						prefix: this.trans.instant('PROCESSES.DETAIL.BROWSER'),
						value: deviceBrowserSummary(device),
					});
					rows.push({
						prefix: 'IP',
						value: device.ip?.trim() ? device.ip : '-',
						lockMode,
					});
					const created = formatDeviceCreatedAtDisplay(device.created_at);
					if (created) {
						rows.push({
							prefix: this.trans.instant('PROCESSES.DETAIL.DATE'),
							value: created,
						});
					}
				}

				const marker: IMapMarker = {
					id: `loc-${locationIndex}-${device?.uuid ?? 'na'}`,
					title,
					label: String(ordinal),
					type: 'text',
					position: { lat, lng },
					status: 'default',
					data: { device, deviceIndex, locationIndex },
					infoWindow: {
						component: MapDeviceInfoComponent,
						input: {
							title,
							rows,
							onViewDetails: device ? () => this.viewDeviceDetails.emit(device) : undefined,
							onBlockUnlock:
								device && lockMode
									? () => this.blockUnlockClick.emit({ action: lockMode, device })
									: undefined,
						},
					},
				};
				return marker;
			})
			.filter((marker): marker is IMapMarker => marker !== null);

		this.applySearchFilter();
	}

	onSearchInput(event: Event | string): void {
		const value =
			typeof event === 'string' ? event : ((event?.target as HTMLInputElement | null)?.value ?? '');
		this.searchText = value;
		this.showSearchMenu = true;
		this.applySearchFilter();
	}

	onSearchSelect(option: ISelect | ISelect[]): void {
		this.showSearchMenu = false;
		this.selectedOption.patchValue(null);
		const selected = Array.isArray(option) ? option[0] : option;
		if (!selected?.id) {
			return;
		}
		const marker = this.markerPositions.find((item) => item.id === selected.id);
		if (marker) {
			this.filteredMarkers = [marker];
			this.searchText = marker.title || '';
		}
	}

	private applySearchFilter(): void {
		const filter = this.searchText.toLowerCase().trim();
		this.filteredMarkers = !filter
			? [...this.markerPositions]
			: this.markerPositions.filter((marker) => marker.title?.toLowerCase().includes(filter));
	}
}
